import { useState } from 'react';
import type { OidcClientInfo } from '@shared/types';
import { formatRelative } from '@shared/format';
import { useAuthStore } from '../app/authStore';
import { ApiError, api, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, CardHeader, CopyField, PageHeader } from '../components/ui';
import { Field, Input, Notice } from '../components/form';
import { ConfirmDialog, Modal, useToast } from '../components/feedback';
import { Table, type Column } from '../components/table';
import { Icon } from '../components/Icon';

interface Endpoints {
  issuer: string;
  discoveryUrl: string;
  jwksUrl: string;
  authorizationEndpoint: string;
  tokenEndpoint: string;
  userinfoEndpoint: string;
  supportedScopes: string[];
}

interface ListResponse {
  clients: OidcClientInfo[];
  endpoints: Endpoints;
}

/**
 * 应用与接入（OIDC 客户端）。
 *
 * `client_secret` 只在创建与轮换的响应里出现一次，落库的只有哈希 ——
 * 因此界面上不会、也不该提供「查看密钥」。
 */
export function ApplicationsPage() {
  const can = useAuthStore((state) => state.can);
  const toast = useToast();
  const list = useAsync<ListResponse>(() => api.get('/api/applications'), []);

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [redirectUris, setRedirectUris] = useState('');
  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<{ clientId: string; clientSecret: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<OidcClientInfo | null>(null);

  const manage = can('app.manage');
  const endpoints = list.data?.endpoints;

  async function run(action: () => Promise<unknown>, message: string) {
    try {
      await action();
      toast.push(message, 'ok');
    } catch (error) {
      toast.push(describeError(error), 'danger');
    }
  }

  const columns: Column<OidcClientInfo>[] = [
    {
      key: 'name',
      header: '应用',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{row.name}</p>
          <p className="truncate font-mono text-xs text-fg-muted">{row.clientId}</p>
        </div>
      ),
    },
    {
      key: 'redirects',
      header: '回跳地址',
      render: (row) => (
        <ul className="space-y-0.5">
          {row.redirectUris.map((uri) => (
            <li key={uri} className="truncate font-mono text-xs text-fg-muted">
              {uri}
            </li>
          ))}
        </ul>
      ),
    },
    {
      key: 'scopes',
      header: 'Scope',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.scopes.map((scope) => (
            <Badge key={scope}>{scope}</Badge>
          ))}
        </div>
      ),
    },
    {
      key: 'status',
      header: '状态',
      render: (row) => (
        <div className="space-y-1">
          {row.status === 'active' ? <Badge tone="ok">启用</Badge> : <Badge tone="warn">已停用</Badge>}
          <p className="text-xs text-fg-subtle">最近使用 {formatRelative(row.lastUsedAt)}</p>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '操作',
      hideOnMobile: true,
      render: (row) =>
        manage ? (
          <div className="flex flex-wrap gap-1">
            <Button
              size="sm"
              variant="link"
              onClick={() =>
                void run(async () => {
                  const result = await api.post<{ clientId: string; clientSecret: string }>(
                    `/api/applications/${row.clientId}/secret`,
                  );
                  setCreated(result);
                }, '密钥已轮换，旧密钥立即失效')
              }
            >
              轮换密钥
            </Button>
            <Button
              size="sm"
              variant="link"
              onClick={() =>
                void run(async () => {
                  const result = await api.patch<ListResponse>(`/api/applications/${row.clientId}`, {
                    status: row.status === 'active' ? 'disabled' : 'active',
                  });
                  list.setData(result);
                }, row.status === 'active' ? '应用已停用' : '应用已启用')
              }
            >
              {row.status === 'active' ? '停用' : '启用'}
            </Button>
            <Button size="sm" variant="link" onClick={() => setConfirmDelete(row)}>
              <span className="text-danger">删除</span>
            </Button>
          </div>
        ) : (
          <span className="text-xs text-fg-subtle">—</span>
        ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="应用与接入"
        description="控制台是一个标准 OIDC Provider：任何支持 OIDC 的系统都能接入，用同一套账号登录。"
        action={
          manage ? (
            <Button
              variant="primary"
              icon="plus"
              onClick={() => {
                setFieldErrors({});
                setCreateOpen(true);
              }}
            >
              新建应用
            </Button>
          ) : null
        }
      />

      <Card>
        <CardHeader title="接入应用" hint="client_secret 只在创建或轮换时显示一次" />
        <Table
          caption="OIDC 接入应用"
          columns={columns}
          rows={list.data?.clients ?? []}
          getKey={(row) => row.clientId}
          loading={list.loading}
          empty={{
            title: '还没有接入应用',
            description: '新建一个应用并把发现文档地址交给对方即可完成接入。',
          }}
        />
      </Card>

      {endpoints ? (
        <Card>
          <CardHeader title="接入信息" hint="交给接入方即可，不需要额外配置" />
          <div className="grid gap-4 md:grid-cols-2">
            <CopyField label="Issuer" value={endpoints.issuer} />
            <CopyField label="发现文档" value={endpoints.discoveryUrl} hint="多数 OIDC 客户端只需要这一个地址" />
            <CopyField label="JWKS" value={endpoints.jwksUrl} hint="接入方用这里的公钥离线验签" />
            <CopyField label="UserInfo" value={endpoints.userinfoEndpoint} />
          </div>
          <div className="mt-4">
            <Notice tone="info">
              支持的 scope：{endpoints.supportedScopes.map((scope) => `\`${scope}\``).join('、')}。
              其中 <code className="font-mono">org</code> 会返回组织与组织内角色，供下游系统做数据隔离。
            </Notice>
          </div>
        </Card>
      ) : null}

      {/* ------------------------------------------------------- 新建应用 */}
      <Modal
        open={createOpen}
        title="新建接入应用"
        description="回跳地址必须精确匹配，且必须是 https（本地调试可用 http://localhost）。"
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <Button onClick={() => setCreateOpen(false)} disabled={busy}>
              取消
            </Button>
            <Button
              variant="primary"
              loading={busy}
              onClick={async () => {
                setBusy(true);
                setFieldErrors({});
                try {
                  const result = await api.post<{ client: OidcClientInfo; endpoints: Endpoints }>(
                    '/api/applications',
                    {
                      name: name.trim(),
                      redirectUris: redirectUris
                        .split(/[\n,]/)
                        .map((line) => line.trim())
                        .filter((line) => line !== ''),
                      scopes: ['profile', 'email', 'org'],
                    },
                  );
                  setCreated({
                    clientId: result.client.clientId,
                    clientSecret: result.client.clientSecret ?? '',
                  });
                  setName('');
                  setRedirectUris('');
                  setCreateOpen(false);
                  await list.reload();
                } catch (error) {
                  if (error instanceof ApiError) {
                    setFieldErrors(error.fieldErrors);
                    if (Object.keys(error.fieldErrors).length === 0) toast.push(error.message, 'danger');
                  } else {
                    toast.push(describeError(error), 'danger');
                  }
                } finally {
                  setBusy(false);
                }
              }}
            >
              创建
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="应用名称" htmlFor="app-name" required error={fieldErrors.name}>
            <Input id="app-name" value={name} onChange={setName} placeholder="运营监管平台" />
          </Field>
          <Field
            label="回跳地址"
            htmlFor="app-redirects"
            required
            error={fieldErrors.redirectUris}
            hint="一行一个。例如 https://havorix.work/auth/callback"
          >
            <textarea
              id="app-redirects"
              value={redirectUris}
              onChange={(event) => setRedirectUris(event.target.value)}
              rows={3}
              className="w-full rounded-control border border-line-strong bg-panel px-4 py-2 font-mono text-[13px] text-fg placeholder:text-fg-placeholder focus:border-brand"
              placeholder="https://havorix.work/auth/callback"
            />
          </Field>
        </div>
      </Modal>

      {/* --------------------------------------------------- 密钥一次性展示 */}
      <Modal
        open={created !== null}
        title="请立即保存客户端密钥"
        description="它只会显示这一次。我们用哈希存储，之后无法再查看。"
        size="lg"
        onClose={() => setCreated(null)}
        footer={
          <Button variant="primary" onClick={() => setCreated(null)}>
            我已妥善保存
          </Button>
        }
      >
        {created ? (
          <div className="space-y-4">
            <CopyField label="client_id" value={created.clientId} />
            <CopyField label="client_secret" value={created.clientSecret} hint="轮换密钥会让旧密钥立即失效。" />
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={confirmDelete !== null}
        title={`删除应用「${confirmDelete?.name ?? ''}」？`}
        description="该应用的未完成授权请求与刷新令牌会一并失效，接入方将立即无法登录。"
        confirmLabel="删除应用"
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          const target = confirmDelete;
          setConfirmDelete(null);
          if (!target) return;
          void run(async () => {
            const result = await api.delete<ListResponse>(`/api/applications/${target.clientId}`);
            list.setData(result);
          }, '应用已删除');
        }}
      />

      <p className="flex items-center gap-1.5 text-xs text-fg-subtle">
        <Icon name="help" className="h-3.5 w-3.5" />
        授权采用 authorization code + PKCE（S256），不使用隐式模式。
      </p>
    </div>
  );
}
